import {
    type AnyTeaser,
    type DefaultTeaser,
    type Metadata,
    type Teaser,
    type Teasers,
} from '@@api/services/metadata/schemas';
import { type Tenant } from '@@api/services/tenant/schemas';
import { DEFAULT_TENANT_ID } from '@@containers/metadata/constants';
import { getTeasersFromMetadata } from '@@containers/metadata/utils';
import { getDefaultVariant, getVariantById } from '@@containers/TenantSpecific/arrayUtils';

import { DEFAULT_TEASER_ID, type SOCIAL_TEASER_ID, VARIANTS_KEY } from './constants';

type TeaserId = typeof DEFAULT_TEASER_ID | typeof SOCIAL_TEASER_ID;

export const getDefaultTeaserVariant = (teasers?: Metadata['teasers']) =>
    teasers && getDefaultVariant(teasers)[VARIANTS_KEY][DEFAULT_TEASER_ID];

const getTeaserVariantsFromTeasers = (
    teaser: Metadata['teasers'],
    tenantVariantId: Tenant['id'],
): Teasers => getVariantById(teaser, tenantVariantId);

const getTeaserVariantFromTeaser = <T>(teaser: Teasers, variantId: TeaserId): T =>
    // @ts-expect-error TS7053 Issue #4120
    teaser[VARIANTS_KEY][variantId];

const getTeaserFromMetadataTeaser = <T>(
    teaser: Metadata['teasers'],
    tenantVariantId: Tenant['id'],
    teaserVariantId: TeaserId,
): T =>
    getTeaserVariantFromTeaser<T>(
        getTeaserVariantsFromTeasers(teaser, tenantVariantId),
        teaserVariantId,
    );

const getTenantSpecificTeaserVariant = <T extends Teaser, U extends Metadata>(
    entity: U,
    tenantVariantId: Tenant['id'],
    teaserVariantId: TeaserId,
): U & T => ({
    ...entity,
    ...getTeaserFromMetadataTeaser<T>(
        getTeasersFromMetadata(entity),
        tenantVariantId,
        teaserVariantId,
    ),
});

export const getDefaultTenantDefaultTeaser = <T extends Metadata>(entity: T): T & DefaultTeaser =>
    getTenantSpecificTeaserVariant<DefaultTeaser, T>(entity, DEFAULT_TENANT_ID, DEFAULT_TEASER_ID);

export type DefaultSeoTeaserDiff = {
    tenantId: typeof DEFAULT_TENANT_ID;
    oldTitle: AnyTeaser['title'];
    newTitle: AnyTeaser['title'];
};

export const getDefaultSeoTeaserDiff = (
    prevSeoTeasers: Metadata['seoTeasers'],
    nextSeoTeasers: Metadata['seoTeasers'],
): DefaultSeoTeaserDiff | null => {
    if (!prevSeoTeasers[0] || !nextSeoTeasers[0]) {
        return null;
    }
    const prevTitle = prevSeoTeasers[0].title;
    const nextTitle = nextSeoTeasers[0].title;

    if (prevTitle !== nextTitle) {
        return {
            tenantId: DEFAULT_TENANT_ID,
            oldTitle: prevTitle,
            newTitle: nextTitle,
        };
    }

    return null;
};

// @ts-expect-error TS7006 Issue #4120
export const getSeoTeaserTitle = (titleHeader, title) =>
    [titleHeader, title].filter((str) => Boolean(str)).join(': ');

const VERY_SIGNIFICANT_THRESHOLD = 0.01;
const SIGNIFICANT_THRESHOLD = 0.05;

// t('metaform.teaser.abtest.results.significance.verySignificant')
// t('metaform.teaser.abtest.results.significance.significant')
// t('metaform.teaser.abtest.results.significance.notSignificant')

export const getSignificance = (significance: number | null | undefined) => {
    if (significance == null) {
        return null;
    }

    if (significance <= VERY_SIGNIFICANT_THRESHOLD) {
        return {
            color: 'success' as const,
            label: 'metaform.teaser.abtest.results.significance.verySignificant',
            pValue: significance,
        };
    }

    if (significance < SIGNIFICANT_THRESHOLD) {
        return {
            color: 'warning' as const,
            label: 'metaform.teaser.abtest.results.significance.significant',
            pValue: significance,
        };
    }

    return {
        color: 'error' as const,
        label: 'metaform.teaser.abtest.results.significance.notSignificant',
        pValue: significance,
    };
};
